import { TFunction } from 'i18next';
import {
  FecRTP,
  MpegProFecLevel,
  SRTMode,
  SrtPathRedundancyMode,
  StreamEncapsulation,
  StreamShaping,
} from 'src/js/pages/streaming/stream/stream-models';
import { isEvenNumber } from 'src/js/validator/number-validator-helper';
import { autoAssignOrNumberOrEmpty, ipOrHostname } from 'src/js/validator/validator';
import * as yup from 'yup';

/*
 * Schema for the form
 */
const regExpYaml = /^[^,]*$/; // no commas
const portNumber = (t: TFunction) =>
  yup
    .number()
    .required(t('validation.required'))
    .typeError(t('validation.stream.portNumber'))
    .min(1, t('validation.stream.portNumber'))
    .max(65535, t('validation.stream.portNumber'));

export const streamValidationSchema = (t: TFunction) =>
  yup.object().shape({
    name: yup.string().max(63, t('validation.maxLength', { length: 63 })),
    protocol: yup.string(),
    address: yup
      .string()
      .when(['encapsulation', 'srtMode'], {
        is: (encapsulation, srtMode) => {
          if (
            encapsulation === StreamEncapsulation.SRT &&
            (srtMode === SRTMode.RENDEZVOUS || srtMode === SRTMode.CALLER)
          ) {
            return true;
          }
          return false;
        },
        then: ipOrHostname(t),
      })
      .when(['encapsulation'], {
        is: (encapsulation) => {
          if (encapsulation === StreamEncapsulation.UDP || encapsulation === StreamEncapsulation.RTP) {
            return true;
          }
          return false;
        },
        then: ipOrHostname(t),
      }),
    srtRedundancyPath2Address: yup.string().when(['encapsulation', 'srtMode', 'srtRedundancyMode'], {
      is: (encapsulation, srtMode, srtRedundancyMode) => {
        if (
          encapsulation === StreamEncapsulation.SRT &&
          srtMode === SRTMode.CALLER &&
          (srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_ACTIVE ||
            srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_BACKUP)
        ) {
          return true;
        }
        return false;
      },
      then: ipOrHostname(t),
    }),
    srtRedundancyPath2Port: yup.mixed().when(['encapsulation', 'srtMode', 'srtRedundancyMode'], {
      is: (encapsulation, srtMode, srtRedundancyMode) => {
        if (
          encapsulation === StreamEncapsulation.SRT &&
          srtMode === SRTMode.CALLER &&
          (srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_ACTIVE ||
            srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_BACKUP)
        ) {
          return true;
        }
        return false;
      },
      then: portNumber(t),
    }),
    encapsulation: yup.mixed().oneOf([2, 3, 34, 64], t('validation.stream.encapsulation')),
    overheadPercentage: yup
      .number()
      .when(['encapsulation', 'shaping'], {
        is: (encapsulation) => {
          if (encapsulation === StreamEncapsulation.SRT) {
            return true;
          }
          return false;
        },
        then: yup
          .number()
          .required(t('validation.required'))
          .typeError(t('validation.stream.overheadPercentageSrt'))
          .min(5, t('validation.stream.overheadPercentageSrt'))
          .max(50, t('validation.stream.overheadPercentageSrt')),
      })
      .when(['encapsulation', 'shaping'], {
        is: (encapsulation, shaping) => {
          if (encapsulation !== StreamEncapsulation.SRT && shaping !== StreamShaping.VBR) {
            return true;
          }
          return false;
        },
        then: yup
          .number()
          .required(t('validation.required'))
          .typeError(t('validation.stream.overheadPercentage'))
          .min(5, t('validation.stream.overheadPercentage'))
          .max(100, t('validation.stream.overheadPercentage')),
      }),
    fecRtp: yup.mixed().oneOf([FecRTP.DISABLED, FecRTP.MPEG_PRO_FEC], t('validation.stream.fecMode')),
    level: yup.mixed().when('fecRtp', {
      is: (fecRtp) => fecRtp === FecRTP.MPEG_PRO_FEC,
      then: yup.number().oneOf([MpegProFecLevel.COLUMNS_ONLY, MpegProFecLevel.ROW_AND_COLUMNS]),
    }),
    rows: yup.mixed().when(['fecRtp', 'level'], {
      is: (fecRtp, level) => {
        return fecRtp === FecRTP.MPEG_PRO_FEC && level === MpegProFecLevel.ROW_AND_COLUMNS;
      },
      then: yup
        .number()
        .required(t('validation.required'))
        .typeError(t('validation.stream.rows'))
        .min(4, t('validation.stream.rows'))
        .max(20, t('validation.stream.rows')),
    }),
    columns: yup
      .mixed()
      .when(['fecRtp', 'level'], {
        is: (fecRtp, level) => {
          const checkColumns = fecRtp === FecRTP.MPEG_PRO_FEC && level === MpegProFecLevel.COLUMNS_ONLY;
          return checkColumns;
        },
        then: yup
          .number()
          .required(t('validation.required'))
          .typeError(t('validation.stream.columnsA'))
          .min(1, t('validation.stream.columnsA'))
          .max(20, t('validation.stream.columnsA')),
      })
      .when(['fecRtp', 'level'], {
        is: (fecRtp, level) => {
          const checkColumns = fecRtp === FecRTP.MPEG_PRO_FEC && level === MpegProFecLevel.ROW_AND_COLUMNS;
          return checkColumns;
        },
        then: yup
          .number()
          .required(t('validation.required'))
          .typeError(t('validation.stream.columnsB'))
          .min(4, t('validation.stream.columnsB'))
          .max(20, t('validation.stream.columnsB'))
          .test('rowsColsMixed', t('validation.stream.rowsColsMixed'), function (columns) {
            if (columns * this.parent.rows > 100) {
              return false;
            }
            return true;
          }),
      }),
    // SRT
    srtMode: yup.mixed().oneOf([0, 1, 2], t('validation.stream.srtMode')),
    sourcePort: autoAssignOrNumberOrEmpty(t),
    port: portNumber(t).when('encapsulation', {
      is: StreamEncapsulation.RTP,
      then: portNumber(t).test('is-even', t('validation.stream.portNumberRTPError'), isEvenNumber),
    }),
    latency: yup.number().when('encapsulation', {
      is: StreamEncapsulation.SRT,
      then: yup
        .number()
        .required(t('validation.required'))
        .typeError(t('validation.stream.latencyNumber'))
        .min(20, t('validation.stream.latencyNumber'))
        .max(8000, t('validation.stream.latencyNumber')),
    }),
    encrypted: yup.boolean(),
    passphrase: yup
      .string()
      .when(['encapsulation', 'encKeyLength', 'passphraseSet'], {
        is: (encapsulation, encKeyLength, passphraseSet) =>
          encapsulation == StreamEncapsulation.SRT && encKeyLength > 0 && !passphraseSet,
        then: yup
          .string()
          .required(t('validation.required'))
          .min(10, t('validation.stream.maxPassphraseLength'))
          .max(79, t('validation.stream.maxPassphraseLength')),
      })
      .when(['encapsulation', 'encKeyLength', 'passphraseSet'], {
        is: (encapsulation, encKeyLength, passphraseSet) =>
          encapsulation == StreamEncapsulation.SRT && encKeyLength > 0 && passphraseSet,
        then: yup
          .string()
          .min(10, t('validation.stream.maxPassphraseLength'))
          .max(79, t('validation.stream.maxPassphraseLength')),
      }),
    srtAccessUserName: yup.string().when(['srtStreamId', 'srtAccessControlPublishingIdType'], {
      is: (srtStreamId, srtAccessControlPublishingIdType) => {
        if (srtAccessControlPublishingIdType === 'custom') {
          return false;
        }
        if (srtStreamId) {
          return true;
        }
        return false;
      },
      then: yup.string().matches(regExpYaml, t('validation.stream.noCommas')),
    }),
    srtAccessResourceName: yup.string().when(['srtStreamId', 'srtAccessControlPublishingIdType'], {
      is: (srtStreamId, srtAccessControlPublishingIdType) => {
        if (srtAccessControlPublishingIdType === 'custom') {
          return false;
        }
        if (srtStreamId) {
          return true;
        }
        return false;
      },
      then: yup.string().matches(regExpYaml, t('validation.stream.noCommas')),
    }),
    mtu: yup
      .number()
      .required(t('validation.required'))
      .typeError(t('validation.stream.mtu'))
      .min(268, t('validation.stream.mtu'))
      .max(1500, t('validation.stream.mtu')),
    tos: yup
      .number()
      .typeError(t('validation.stream.tos'))
      .min(0, t('validation.stream.tos'))
      .max(255, t('validation.stream.tos')),
    ttl: yup
      .number()
      .required(t('validation.required'))
      .typeError(t('validation.stream.ttl'))
      .min(1, t('validation.stream.ttl'))
      .max(255, t('validation.stream.ttl')),
    sap: yup.object().shape({
      name: yup.string().when('advertise', {
        is: (val) => {
          // console.log('what is advertise?', val);
          return val;
        },
        then: yup
          .string()
          .required(t('validation.required'))
          .max(63, t('validation.maxLength', { length: 63 })),
      }),
    }),
    videoPids: yup
      .number()
      .typeError(t('validation.stream.videoPids'))
      .min(16, t('validation.stream.videoPids'))
      .max(8190, t('validation.stream.videoPids')),
    audioPids: yup.mixed().test('is-pid-array', t('validation.stream.audioPids'), (pids) => {
      const audioPids = typeof pids === 'string' ? pids.trim().split(',') : [];
      for (let i = 0; i < audioPids.length; i++) {
        if (/\D/.test(audioPids[i].trim())) {
          return false;
        } else if (Number(audioPids[i].trim()) < 16 || Number(audioPids[i].trim()) > 8190) {
          return false;
        }
      }
      return true;
    }),
    metadataPids: yup
      .number()
      .typeError(t('validation.stream.metadataPids'))
      .min(16, t('validation.stream.metadataPids'))
      .max(8190, t('validation.stream.metadataPids')),
    sctePids: yup
      .number()
      .typeError(t('validation.stream.sctePids'))
      .min(16, t('validation.stream.sctePids'))
      .max(8190, t('validation.stream.sctePids')),
    pcrPid: yup
      .number()
      .typeError(t('validation.stream.pcrPid'))
      .min(16, t('validation.stream.pcrPid'))
      .max(8190, t('validation.stream.pcrPid')),
    pmtPid: yup
      .number()
      .typeError(t('validation.stream.pmtPid'))
      .min(16, t('validation.stream.pmtPid'))
      .max(8190, t('validation.stream.pmtPid')),
    tsStreamId: yup
      .number()
      .typeError(t('validation.stream.tsStreamId'))
      .min(0, t('validation.stream.tsStreamId'))
      .max(65535, t('validation.stream.tsStreamId')),
    programNumber: yup
      .number()
      .typeError(t('validation.stream.programNumber'))
      .min(1, t('validation.stream.programNumber'))
      .max(65535, t('validation.stream.programNumber')),
  });
